import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, Circle, CheckCircle2, Loader2, AlertCircle, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * StageProgressionSidebar
 *
 * Replaces the small "Stage X/17" pill with a richer, collapsible vertical
 * list of all 17 stages, grouped by section (Pre-Production → Visualization
 * → Motion → Delivery). Status dots: done=green, active=cyan pulse,
 * processing=amber spinner, failed=red, pending=grey.
 *
 * Polls GET /api/projects/:id/stages every 6s while anything is processing,
 * 20s otherwise. Honors `project.current_stage` passed from the parent so
 * the first paint happens without waiting for the first poll.
 */

const statusStyles = {
  done: { dot: 'bg-emerald-500 border-emerald-500', text: 'text-white/70', icon: CheckCircle2, iconClass: 'text-emerald-400' },
  active: { dot: 'bg-cyan-400 border-cyan-300 animate-pulse', text: 'text-white', icon: Circle, iconClass: 'text-cyan-400 fill-cyan-400' },
  processing: { dot: 'bg-amber-400 border-amber-300 animate-pulse', text: 'text-amber-200', icon: Loader2, iconClass: 'text-amber-400 animate-spin' },
  failed: { dot: 'bg-rose-500 border-rose-400', text: 'text-rose-300', icon: AlertCircle, iconClass: 'text-rose-400' },
  pending: { dot: 'bg-white/10 border-white/20', text: 'text-white/30', icon: Circle, iconClass: 'text-white/15' },
};

function formatDuration(sec) {
  if (!sec || sec < 1) return '';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec - m * 60);
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

function MetricBadge({ metrics }) {
  if (!metrics || Object.keys(metrics).length === 0) return null;
  const parts = [];
  if (metrics.chars !== undefined) parts.push(`${metrics.chars.toLocaleString()} chars`);
  if (metrics.entities !== undefined && metrics.entities > 0) parts.push(`${metrics.entities} entities`);
  if (metrics.total_shots !== undefined && metrics.total_shots > 0) parts.push(`${metrics.total_shots} shots`);
  if (metrics.sketched !== undefined) parts.push(`${metrics.sketched}/${metrics.total} sketched`);
  if (metrics.keyframes !== undefined) parts.push(`${metrics.keyframes}/${metrics.total} KFs`);
  if (metrics.locked !== undefined && metrics.total !== undefined) parts.push(`${metrics.locked}/${metrics.total} locked`);
  if (metrics.videos !== undefined) parts.push(`${metrics.videos}/${metrics.total} videos`);
  if (metrics.cast !== undefined) {
    const bits = [];
    if (metrics.cast > 0) bits.push(`${metrics.cast} cast`);
    if (metrics.costumes > 0) bits.push(`${metrics.costumes} costumes`);
    if (metrics.locations > 0) bits.push(`${metrics.locations} locations`);
    if (bits.length) parts.push(bits.join(' · '));
  }
  if (parts.length === 0) return null;
  return <span className="text-[10px] text-white/40 font-mono" data-testid="stage-metric">{parts.join(' · ')}</span>;
}

// eslint-disable-next-line no-unused-vars
export default function StageProgressionSidebar({ projectId, currentStage, authToken, onStageClick }) {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('eip_stage_sidebar_collapsed') === '1'; } catch (e) { return false; }
  });
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const fetchingRef = useRef(false);
  const dataRef = useRef(null);

  // Keep dataRef in sync so the polling closure can read fresh data without
  // triggering effect re-runs.
  useEffect(() => { dataRef.current = data; }, [data]);

  useEffect(() => {
    if (!projectId) return;
    // Only cancel on unmount / projectId swap — NOT on every parent re-render.
    // The prior implementation had `currentStage` in deps which caused parent
    // project polling to cancel in-flight /stages responses and left the UI
    // stuck on "Loading stages…". See iter-104 RCA.
    const abortController = new AbortController();

    async function fetchStages() {
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      try {
        const res = await axios.get(`${API}/projects/${projectId}/stages`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
          signal: abortController.signal,
        });
        setData(res.data);
      } catch (err) {
        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          // eslint-disable-next-line no-console
          console.warn('[StageProgression] fetch failed', err?.message);
        }
      } finally {
        fetchingRef.current = false;
      }
    }

    fetchStages();
    const interval = setInterval(() => {
      // Read from ref so we don't go stale — tracks the latest data without
      // forcing another effect re-run.
      const hasProcessing = dataRef.current?.stages?.some(
        s => s.status === 'processing' || s.status === 'active'
      );
      if (hasProcessing) fetchStages();
      else if (Math.random() < 0.33) fetchStages();
    }, 6000);

    return () => { abortController.abort(); clearInterval(interval); };
  }, [projectId, authToken]);

  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem('eip_stage_sidebar_collapsed', next ? '1' : '0'); } catch (e) {}
      return next;
    });
  };

  if (!data) {
    return (
      <div className="px-3 py-2 text-[10px] text-white/40 font-mono border-b border-ai-border" data-testid="stage-sidebar-loading">
        Loading stages…
      </div>
    );
  }

  // Header strip is ALWAYS visible (this IS the sidebar); stage list is collapsible.
  const percent = data.percent_complete || 0;
  const done = data.completed || 0;
  const total = data.total_stages || 17;

  // Group stages by their `group` field, preserving order.
  const groupsOrder = [];
  const grouped = {};
  for (const s of data.stages) {
    if (!grouped[s.group]) {
      grouped[s.group] = [];
      groupsOrder.push(s.group);
    }
    grouped[s.group].push(s);
  }

  return (
    <div className="border-b border-ai-border" style={{ background: 'rgba(11, 15, 25, 0.6)' }} data-testid="stage-sidebar">
      {/* Header — click to expand/collapse */}
      <button
        type="button"
        onClick={toggleCollapsed}
        className="w-full px-3 sm:px-4 py-2 flex items-center gap-3 hover:bg-white/5 transition-colors text-left"
        data-testid="stage-sidebar-toggle"
      >
        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-mono text-[11px] font-bold" style={{ background: 'rgba(14,165,233,0.2)', color: '#7dd3fc' }}>
          {data.current_stage}/{total}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-white truncate" data-testid="stage-sidebar-current">
            {data.next_action || `Stage ${data.current_stage}`}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(14,165,233,0.12)' }}>
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${percent}%`, background: 'linear-gradient(90deg, #0EA5E9, #14B8A6)' }} />
            </div>
            <span className="text-[10px] text-white/50 font-mono">{done}/{total} · {percent}%</span>
          </div>
        </div>
        {collapsed ? <ChevronDown className="w-4 h-4 text-white/40" /> : <ChevronUp className="w-4 h-4 text-white/40" />}
      </button>

      {/* Collapsible stage list */}
      {!collapsed && (
        <div className="px-2 sm:px-3 pb-2 space-y-2" data-testid="stage-sidebar-list">
          {groupsOrder.map(group => {
            const isGroupCollapsed = collapsedGroups[group];
            const stages = grouped[group];
            const groupDone = stages.filter(s => s.status === 'done').length;
            return (
              <div key={group}>
                <button
                  type="button"
                  onClick={() => setCollapsedGroups(g => ({ ...g, [group]: !g[group] }))}
                  className="w-full flex items-center gap-1.5 px-1 py-1 text-[10px] uppercase tracking-wider text-white/40 hover:text-white/70"
                  data-testid={`stage-group-toggle-${group.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {isGroupCollapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                  <span className="font-semibold">{group}</span>
                  <span className="font-mono opacity-70">{groupDone}/{stages.length}</span>
                </button>
                {!isGroupCollapsed && (
                  <div className="ml-2 border-l border-white/5 pl-2 space-y-0.5">
                    {stages.map(st => {
                      const s = statusStyles[st.status] || statusStyles.pending;
                      return (
                        <button
                          key={st.slug}
                          type="button"
                          onClick={() => onStageClick && onStageClick(st)}
                          className={`w-full flex items-start gap-2 px-1.5 py-1 rounded text-left hover:bg-white/5 transition-colors ${st.num === data.current_stage ? 'bg-white/[0.04]' : ''}`}
                          data-testid={`stage-row-${st.slug}`}
                          title={st.description}
                        >
                          <span className="mt-0.5 flex-shrink-0">
                            <s.icon className={`w-3 h-3 ${s.iconClass}`} />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className={`text-[11px] font-medium ${s.text} leading-tight block truncate`}>
                              <span className="text-white/30 font-mono mr-1.5">{String(st.num).padStart(2, '0')}</span>
                              {st.name}
                            </span>
                            <span className="flex items-center gap-1.5 mt-0.5">
                              <MetricBadge metrics={st.metrics} />
                              {st.duration_seconds ? (
                                <span className="text-[10px] text-white/30 font-mono inline-flex items-center gap-0.5">
                                  <Clock className="w-2.5 h-2.5" />
                                  {formatDuration(st.duration_seconds)}
                                </span>
                              ) : null}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
